import React from "react";
import { UserCheck, TrendingUp, BadgeCheck } from "lucide-react";
import { DataCell } from "@/components/DataCell";
import { useTranslation } from "react-i18next";
import {
  Student,
  JoinTypeEnum,
  PredictionIndexEnum,
  GoingStatusEnum,
  YesNoEnum,
} from "@/__generated__/types";
import { CircleIcon } from "@/components/DashboardOverviewDataCells/CircleIcon";

interface BentoSectionProps {
  student: Pick<Student, "studentOrganizationStatus">;
}

export const BentoSection: React.FC<BentoSectionProps> = ({ student }) => {
  const { t } = useTranslation();

  const getCommunityStatus = () => {
    const status = student.studentOrganizationStatus;
    const inCommunity = status?.inCommunity === YesNoEnum.Yes;

    if (!inCommunity) return "never-joined";
    if (inCommunity && status?.interested === YesNoEnum.No)
      return "left-community";
    return "currently-joined";
  };

  const communityStatus = getCommunityStatus();

  const getSourceTooltipContent = () => {
    return (
      <div>
        <div className="mb-2">{t("studentCard.bento.sourceToolTip")}</div>
        <ul className="list-inside list-disc space-y-1 pl-2">
          <li>{t("studentCard.bento.sourceToolTipBullet1")}</li>
          <li>{t("studentCard.bento.sourceToolTipBullet2")}</li>
          <li>{t("studentCard.bento.sourceToolTipBullet3")}</li>
        </ul>
      </div>
    );
  };

  const getCommunityStatusTooltipContent = () => {
    return (
      <div>
        <div className="mb-2">
          {t("studentCard.bento.communityStatusToolTip")}
        </div>
        <ul className="list-inside list-disc pl-2">
          <li>{t("studentCard.bento.communityStatusToolTipBullet1")}</li>
        </ul>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {/* Source DataCell */}
      <DataCell
        className="min-h-[136px]"
        title={
          communityStatus === "currently-joined"
            ? t("studentCard.bento.source")
            : t("studentCard.bento.status")
        }
        tooltipText={
          communityStatus === "currently-joined"
            ? getSourceTooltipContent()
            : getCommunityStatusTooltipContent()
        }
        middleValue={(() => {
          if (communityStatus === "never-joined") {
            return t("studentCard.bento.notInCommunity");
          }
          if (communityStatus === "left-community") {
            return t("studentCard.bento.statusLeftCommunity");
          }
          switch (student.studentOrganizationStatus?.joinType) {
            case JoinTypeEnum.Csv:
              return t("studentCard.bento.communityMatch");
            case JoinTypeEnum.Branch:
              return t("studentCard.bento.invited");
            case JoinTypeEnum.CompetitorMatch:
              return t("studentCard.bento.competitorMatch");
            case JoinTypeEnum.Organic:
              return t("studentCard.bento.organic");
            default:
              return t("studentCard.bento.notInCommunity");
          }
        })()}
        bottomValue={(() => {
          if (communityStatus === "never-joined") {
            return t("studentCard.bento.crmDataOnly");
          }
          if (communityStatus === "left-community") {
            const becameUninterestedOn =
              student.studentOrganizationStatus?.becameUninterestedOn;
            const joinedOn = student.studentOrganizationStatus?.joinedOn;

            if (becameUninterestedOn) {
              const date = new Date(becameUninterestedOn);
              return t("studentCard.bento.leftOn", {
                date: date.toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }),
              });
            }
            if (joinedOn) {
              return t("studentCard.bento.leftOnUnavailable");
            }
            return "";
          }
          const joinType = student.studentOrganizationStatus?.joinType;
          const joinedOn = student.studentOrganizationStatus?.joinedOn;

          if (!joinType) return t("metrics.comparisons.noData");
          if (!joinedOn) return "";

          const date = new Date(joinedOn);
          return `${t("studentCard.bento.joined")} ${date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}`;
        })()}
        icon={
          <CircleIcon color="indigo">
            <UserCheck />
          </CircleIcon>
        }
      />

      {/* Commit Index DataCell */}
      <DataCell
        className="min-h-[136px]"
        title={t("studentCard.bento.commitIndex")}
        tooltipText={t("studentCard.bento.commitIndexToolTip")}
        middleValue={(() => {
          const commitIndex = student.studentOrganizationStatus?.commitIndex;
          if (!commitIndex) return "--";
          switch (commitIndex) {
            case PredictionIndexEnum.High:
              return t("studentCard.bento.high");
            case PredictionIndexEnum.Medium:
              return t("studentCard.bento.medium");
            case PredictionIndexEnum.Low:
              return t("studentCard.bento.low");
            default:
              return commitIndex;
          }
        })()}
        bottomValue={(() => {
          const commitIndex = student.studentOrganizationStatus?.commitIndex;
          const commitIndexChangedOn =
            student.studentOrganizationStatus?.commitIndexChangedOn;

          if (!commitIndex) return t("metrics.comparisons.noData");
          if (!commitIndexChangedOn) return "";

          const date = new Date(commitIndexChangedOn);
          return `${t("studentCard.bento.updated")} ${date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}`;
        })()}
        icon={
          <CircleIcon color="yellow">
            <TrendingUp />
          </CircleIcon>
        }
      />

      {/* Going Status DataCell */}
      <DataCell
        className="min-h-[136px]"
        title={t("studentCard.bento.goingStatus")}
        tooltipText={t("studentCard.bento.goingStatusToolTip")}
        middleValue={(() => {
          const goingStatus = student.studentOrganizationStatus?.goingStatus;
          if (!goingStatus) return "--";
          switch (goingStatus) {
            case GoingStatusEnum.Going:
              return t("studentCard.bento.going");
            case GoingStatusEnum.Notgoing:
              return t("studentCard.bento.notGoing");
            case GoingStatusEnum.Undecided:
              return t("studentCard.bento.undecided");
            default:
              return goingStatus;
          }
        })()}
        bottomValue={(() => {
          const status = student.studentOrganizationStatus;
          const goingStatus = status?.goingStatus;

          if (!goingStatus) return t("metrics.comparisons.noData");
          const dateStr = status?.goingStatusChangedOn || status?.joinedOn;
          if (!dateStr) return "";

          const date = new Date(dateStr);
          return `${t("studentCard.bento.asOf")} ${date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}`;
        })()}
        icon={
          <CircleIcon color="emerald">
            <BadgeCheck />
          </CircleIcon>
        }
      />
    </div>
  );
};
